<script setup lang="ts">
import { computed, ref } from "vue";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";
import { Loader2, PencilLine } from "@lucide/vue";

import { useModalStore } from "@/stores/modals/modals.store";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import { useProjectRevisionsStore } from "../stores/projectRevisions.store";
import type { ProjectRevision } from "../types/projectRevision.types";

// Rename a save point. Only the label moves — the state the revision holds, its Marten pin and the
// instant it froze are all untouched and unreachable from here, so this is safe on the CURRENT
// revision too (the name is how the toolbar and the preview banner refer to it, and a save point
// that turned out to be worth keeping usually deserves a better name than the one it was given).
// Admins only; the caller gates the button and the Hasura permission enforces it.
const props = defineProps<{
  projectId: string;
  revision: ProjectRevision;
}>();

const toast = useToast();
const { closeByName } = useModalStore();
const revisionsStore = useProjectRevisionsStore();

const revisionName = ref(props.revision.name);
const isRenaming = ref(false);

// Matches the column's own check constraint, so the input refuses what Postgres would.
const nameError = computed(() => (revisionName.value.trim() ? "" : "Give the revision a name"));
const isUnchanged = computed(() => revisionName.value.trim() === props.revision.name);

const cancel = () => closeByName("RenameProjectRevisionModal", { action: "cancel" });

const confirm = async () => {
  if (nameError.value || isUnchanged.value || isRenaming.value) return;
  isRenaming.value = true;
  try {
    const name = revisionName.value.trim();
    await revisionsStore.renameRevision(props.projectId, props.revision.id, name);
    toast.success(`Revision renamed to “${name}”.`);
    closeByName("RenameProjectRevisionModal", { action: "renamed", name });
  } catch (e) {
    Sentry.captureException(e);
    toast.error("Could not rename the revision. Please try again.");
  } finally {
    isRenaming.value = false;
  }
};

const btnBase =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";
</script>

<template>
  <div class="flex flex-col gap-4">
    <h2 class="t-sh4 text-type-default">Rename revision</h2>

    <p class="t-small text-type-muted">
      Only the name changes. Everything this save point holds — its materials, mappings and project information — stays exactly as
      it is, and everyone on the project sees the new name.
    </p>

    <TextInputNew
      v-model="revisionName"
      label="Revision name"
      :error-msg="nameError"
      :disabled="isRenaming"
      required />

    <div class="gap-px-8 flex justify-end">
      <button
        type="button"
        :disabled="isRenaming"
        :class="[
          btnBase,
          'bg-slate-0 text-type-default border border-slate-200 hover:bg-slate-100',
          { 'interactive-disabled': isRenaming },
        ]"
        data-cy="rename-revision-cancel"
        @click="cancel">
        Cancel
      </button>
      <button
        type="button"
        :disabled="!!nameError || isUnchanged || isRenaming"
        :class="[
          btnBase,
          !nameError && !isUnchanged && !isRenaming
            ? 'bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover text-white'
            : 'text-type-muted bg-slate-200',
          { 'interactive-disabled': !!nameError || isUnchanged || isRenaming },
        ]"
        data-cy="rename-revision-confirm"
        @click="confirm">
        <Loader2
          v-if="isRenaming"
          class="icon-sm animate-spin"
          :stroke-width="1.5" />
        <PencilLine
          v-else
          class="icon-sm"
          :stroke-width="1.5" />
        {{ isRenaming ? "Renaming…" : "Rename revision" }}
      </button>
    </div>
  </div>
</template>
